import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { fieldErrorsOf } from "@repo/api-client";
import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { activateInvite } from "../shell/staffApi";

/**
 * Public accept-invite activation screen (staff-app-shell). The invitee arrives via the emailed link
 * `/activate?tenant=<tenantId>&token=<token>`, chooses a password, and this posts to the PUBLIC
 * `POST /api/v1/staff/invites/activate` (no bearer token — the single-use invite token is the
 * credential). On success it points them at /login to sign in with their new password.
 */
export function Activate() {
  const [params] = useSearchParams();
  const tenantId = params.get("tenant") ?? "";
  const token = params.get("token") ?? "";
  const { t } = useTranslation();
  const [done, setDone] = useState(false);

  const schema = useMemo(
    () =>
      z
        .object({
          password: z.string().min(8, t("activate.error.passwordShort")),
          confirm: z.string(),
        })
        .refine((v) => v.password === v.confirm, {
          path: ["confirm"],
          message: t("activate.error.mismatch"),
        }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
    mode: "onSubmit",
  });

  const mutation = useMutation({
    mutationFn: (password: string) => activateInvite({ tenantId, token, password }),
    onSuccess: () => setDone(true),
    onError: (err) => {
      for (const [field, message] of Object.entries(fieldErrorsOf(err))) {
        if (field === "password") form.setError("password", { type: "server", message });
      }
    },
  });

  const submit = form.handleSubmit((data) => mutation.mutate(data.password));

  if (!tenantId || !token) {
    return (
      <main className={css({ p: "8", maxW: "sm", mx: "auto" })}>
        <h1 className={css({ fontSize: "2xl", fontWeight: "bold" })}>{t("activate.title")}</h1>
        <p role="alert" className={css({ color: "fg.muted", mt: "4" })}>
          {t("activate.invalidLink")}
        </p>
      </main>
    );
  }

  if (done) {
    return (
      <main
        className={css({
          p: "8",
          maxW: "sm",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "4",
        })}
      >
        <h1 className={css({ fontSize: "2xl", fontWeight: "bold" })}>{t("activate.title")}</h1>
        <output className={css({ color: "fg.muted" })}>{t("activate.success")}</output>
        <Link to="/login" className={css({ color: "accent.default", textDecoration: "underline" })}>
          {t("activate.goToLogin")}
        </Link>
      </main>
    );
  }

  return (
    <main
      className={css({
        p: "8",
        maxW: "sm",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "4",
      })}
    >
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold" })}>{t("activate.title")}</h1>
      <p className={css({ color: "fg.muted" })}>{t("activate.subtitle")}</p>
      <form
        onSubmit={submit}
        className={css({ display: "flex", flexDirection: "column", gap: "3" })}
        noValidate
      >
        <label className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
          <span>{t("activate.password")}</span>
          <input
            type="password"
            autoComplete="new-password"
            {...form.register("password")}
            className={inputClass}
            aria-invalid={form.formState.errors.password ? true : undefined}
          />
          {form.formState.errors.password ? (
            <span role="alert" className={errorClass}>
              {form.formState.errors.password.message}
            </span>
          ) : null}
        </label>
        <label className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
          <span>{t("activate.confirm")}</span>
          <input
            type="password"
            autoComplete="new-password"
            {...form.register("confirm")}
            className={inputClass}
            aria-invalid={form.formState.errors.confirm ? true : undefined}
          />
          {form.formState.errors.confirm ? (
            <span role="alert" className={errorClass}>
              {form.formState.errors.confirm.message}
            </span>
          ) : null}
        </label>
        {mutation.isError ? (
          <p role="alert" className={errorClass}>
            {t("activate.error.failed")}
          </p>
        ) : null}
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? t("activate.submitting") : t("activate.submit")}
        </Button>
      </form>
    </main>
  );
}

const inputClass = css({
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "md",
  px: "3",
  py: "2",
});

const errorClass = css({ color: "red.11", fontSize: "sm" });
